import { Activity, Pencil, Plug, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { McpServer } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { McpHealthBadge } from "@/features/mcps/components/McpHealthBadge";
import {
  useDeactivateMcp,
  useMcpHealthCheck,
} from "@/features/mcps/hooks/useMcps";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  mcp: McpServer;
  onEdit: () => void;
}

export function McpCard({ mcp, onEdit }: Props) {
  const deactivate = useDeactivateMcp();
  const healthCheck = useMcpHealthCheck();
  const canManage = useCan(Action.MCP_SERVER_UPDATED, {
    owner_team_id: mcp.owner_team_id,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleDeactivate() {
    try {
      await deactivate.mutateAsync(mcp.id);
      toast.success("MCP desativado");
      setConfirmOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleHealthCheck() {
    try {
      await healthCheck.mutateAsync(mcp.id);
      toast.success("Health-check executado");
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li className="flex flex-col rounded-md border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Plug className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-base font-semibold">{mcp.name}</h2>
            <p className="text-xs font-mono text-muted-foreground">
              {mcp.url ?? "—"}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline">{mcp.transport}</Badge>
          {!mcp.is_active ? (
            <Badge variant="destructive">Inativo</Badge>
          ) : null}
        </div>
      </div>
      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
        {mcp.description ?? "Sem descrição"}
      </p>
      <div className="mt-3 flex items-center gap-2">
        <McpHealthBadge
          status={mcp.last_health_status}
          checkedAt={mcp.last_health_check_at}
        />
        {mcp.has_credentials ? (
          <Badge variant="outline" className="gap-1 text-xs">
            Credenciais setadas
          </Badge>
        ) : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleHealthCheck}
          disabled={healthCheck.isPending}
        >
          <Activity className="h-3 w-3" />
          Health check agora
        </Button>
        {canManage ? (
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Pencil className="h-3 w-3" />
            Editar
          </Button>
        ) : null}
        {canManage && mcp.is_active ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-danger-700 hover:text-danger-700"
            onClick={() => setConfirmOpen(true)}
            disabled={deactivate.isPending}
          >
            <Trash2 className="h-3 w-3" />
            Desativar
          </Button>
        ) : null}
      </div>

      <ConfirmDestructiveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Desativar servidor MCP"
        description="Os agentes que usam este MCP deixarão de ter acesso às suas ferramentas. As credenciais permanecem armazenadas."
        resourceName={mcp.name}
        resourceKind="MCP"
        confirmLabel="Desativar MCP"
        onConfirm={handleDeactivate}
        isPending={deactivate.isPending}
      />
    </li>
  );
}
